import React, { useState, useMemo } from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts';
import CompetitionsTabDrillDownModal from './CompetitionsTabDrillDownModal';

const C = ['#0B3D91', '#E31837', '#2196F3', '#F59E0B', '#10B981', '#8B5CF6'];

export default function PeopleSection({ data }) {
  const [roleFilter, setRoleFilter] = useState('all');
  const [drillPerson, setDrillPerson] = useState(null);
  const [showUnregistered, setShowUnregistered] = useState(false);

  const {
    people_by_role, people_by_gender, qualification_levels, active_status,
    _people, _matchUmpires,
  } = data;

  const roles = Object.keys(people_by_role || {});

  // Build name → match_count lookup from CricClubs match umpires
  const matchUmpireMap = useMemo(() => {
    const map = {};
    (_matchUmpires || []).forEach(mu => {
      if (mu.name) map[mu.name.trim().toLowerCase()] = mu.match_count || 0;
    });
    return map;
  }, [_matchUmpires]);

  // Enrich people rows with match activity where applicable
  const enrichedPeople = useMemo(() => {
    return (_people || []).map(p => {
      const key = `${p.first_name || ''} ${p.last_name || ''}`.trim().toLowerCase();
      const matchCount = matchUmpireMap[key] ?? null;
      return { ...p, match_count: matchCount };
    });
  }, [_people, matchUmpireMap]);

  // Umpires in People file that also appear in match_umpires
  const registeredWithMatches = useMemo(() =>
    enrichedPeople.filter(p => p.role?.toLowerCase().includes('umpire') && p.match_count !== null),
    [enrichedPeople]
  );

  // Match umpires NOT in People registration file
  const unregisteredMatchUmpires = useMemo(() => {
    const registeredNames = new Set(
      enrichedPeople
        .filter(p => p.role?.toLowerCase().includes('umpire'))
        .map(p => `${p.first_name || ''} ${p.last_name || ''}`.trim().toLowerCase())
    );
    return (_matchUmpires || []).filter(mu => !registeredNames.has((mu.name || '').trim().toLowerCase()));
  }, [enrichedPeople, _matchUmpires]);

  const filtered = useMemo(() => {
    if (roleFilter === 'all') return enrichedPeople;
    return enrichedPeople.filter(p => p.role === roleFilter);
  }, [roleFilter, enrichedPeople]);

  const roleData = Object.entries(people_by_role || {}).map(([name, value]) => ({ name, value }));
  const genderData = Object.entries(people_by_gender || {}).filter(([k]) => k).map(([name, value]) => ({ name, value }));
  const qualData = Object.entries(qualification_levels || {}).map(([name, value]) => ({ name, value }));
  const activeData = Object.entries(active_status || {})
    .filter(([k]) => k === 'Yes' || k === 'No')
    .map(([name, value]) => ({ name: name === 'Yes' ? 'Active' : 'Inactive', value }));

  const regionCount = {};
  filtered.forEach(p => { const r = p.region || 'Unknown'; regionCount[r] = (regionCount[r] || 0) + 1; });

  const totalPeople = Object.values(people_by_role || {}).reduce((s, v) => s + v, 0);
  const totalMatchUmpires = (_matchUmpires || []).length;

  return (
    <div data-testid="people-section">
      <div className="section-title">Coaches &amp; Umpires</div>
      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0 0 1rem', maxWidth: '52rem' }}>
        People register from <strong>Final 2025PeopleRecordTemplate UmpireCoaches.xlsx</strong>.
        Umpires are cross-referenced against CricClubs match officials — match counts shown where applicable.
      </p>

      {/* Overlap insight banner */}
      <div className="glass-card" style={{ padding: '0.75rem 1rem', marginBottom: '1.25rem', borderLeft: '3px solid #2196F3' }}>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div>
              <span style={{ fontSize: '1.4rem', fontWeight: 700, color: '#2196F3' }}>{registeredWithMatches.length}</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 6 }}>
                registered umpires also appear as CricClubs match officials
              </span>
            </div>
            <div>
              <span style={{ fontSize: '1.4rem', fontWeight: 700, color: '#F59E0B' }}>{unregisteredMatchUmpires.length}</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 6 }}>
                match officials not in the ICC People registration
              </span>
            </div>
            <div>
              <span style={{ fontSize: '1.4rem', fontWeight: 700, color: '#10B981' }}>{totalMatchUmpires}</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 6 }}>
                total CricClubs match umpires
              </span>
            </div>
          </div>
          {unregisteredMatchUmpires.length > 0 && (
            <button
              type="button"
              onClick={() => setShowUnregistered(v => !v)}
              style={{ fontSize: '0.72rem', color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              {showUnregistered ? 'Hide' : 'Show'} unregistered officials
            </button>
          )}
        </div>

        {showUnregistered && unregisteredMatchUmpires.length > 0 && (
          <div style={{ marginTop: '0.75rem' }}>
            <div style={{ fontSize: '0.72rem', color: '#F59E0B', fontWeight: 600, marginBottom: '0.4rem' }}>
              Match officials not in People registration — consider adding to next ICC submission:
            </div>
            <table style={{ width: '100%', fontSize: '0.72rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
                  <th style={{ padding: '0.2rem 0.5rem 0.2rem 0' }}>Name</th>
                  <th style={{ padding: '0.2rem 0.5rem', textAlign: 'right' }}>Matches Officiated</th>
                </tr>
              </thead>
              <tbody>
                {unregisteredMatchUmpires.sort((a, b) => (b.match_count || 0) - (a.match_count || 0)).map((u, i) => (
                  <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <td style={{ padding: '0.2rem 0.5rem 0.2rem 0', color: 'var(--text-primary)' }}>{u.name}</td>
                    <td style={{ padding: '0.2rem 0.5rem', textAlign: 'right', color: 'var(--text-muted)' }}>{u.match_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Role filter pills */}
      <div className="people-pills">
        <button className={`people-pill ${roleFilter === 'all' ? 'active' : ''}`}
          onClick={() => setRoleFilter('all')} data-testid="people-filter-all">
          All ({totalPeople})
        </button>
        {roles.map(role => (
          <button key={role} className={`people-pill ${roleFilter === role ? 'active' : ''}`}
            onClick={() => setRoleFilter(role)} data-testid={`people-filter-${role.toLowerCase()}`}>
            {role} ({people_by_role[role]})
          </button>
        ))}
      </div>

      <div className="charts-grid">
        <div className="glass-card chart-card">
          <h3>Role Distribution</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={roleData} cx="50%" cy="50%" innerRadius={50} outerRadius={95}
                paddingAngle={3} dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                {roleData.map((_, i) => <Cell key={i} fill={C[i % C.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="glass-card chart-card">
          <h3>Qualification Pyramid</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={qualData} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={80} />
              <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
              <Bar dataKey="value" name="People" radius={[0, 6, 6, 0]}>
                <Cell fill="#4FC3F7" /><Cell fill="#2196F3" /><Cell fill="#0B3D91" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'center' }}>
            Development pathway: Beginner &rarr; Intermediate &rarr; Advanced
          </p>
        </div>
      </div>

      <div className="charts-grid" style={{ marginTop: '1rem' }}>
        <div className="glass-card chart-card">
          <h3>Gender Split</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={genderData} cx="50%" cy="50%" innerRadius={45} outerRadius={90}
                paddingAngle={3} dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                <Cell fill="#0B3D91" /><Cell fill="#E31837" />
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="glass-card chart-card">
          <h3>Active Status</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={activeData} cx="50%" cy="50%" innerRadius={45} outerRadius={90}
                paddingAngle={3} dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                <Cell fill="#10B981" /><Cell fill="#94A3B8" />
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textAlign: 'center' }}>
            Currently active coaches and umpires
          </p>
        </div>
      </div>

      {/* People table */}
      <div className="glass-card" style={{ padding: '0', marginTop: '1.25rem', overflow: 'auto' }}>
        <table className="data-table" data-testid="people-table">
          <thead>
            <tr>
              <th>Name</th><th>Role</th><th>Gender</th><th>Qualification</th><th>Active</th>
              <th className="num" title="Matches officiated in CricClubs data">Matches</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 60).map((p, i) => (
              <tr
                key={i}
                className="drill-row-clickable"
                onClick={() => setDrillPerson(p)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setDrillPerson(p);
                  }
                }}
                tabIndex={0}
                role="button"
              >
                <td style={{ fontWeight: 500 }}>{p.first_name} {p.last_name}</td>
                <td><span className={`role-badge role-${p.role?.toLowerCase()}`}>{p.role}</span></td>
                <td>{p.gender || '\u2014'}</td>
                <td>
                  {p.qualification_level ? (
                    <span className={`qual-badge qual-${p.qualification_level?.toLowerCase()}`}>
                      {p.qualification_level}
                    </span>
                  ) : '\u2014'}
                </td>
                <td>
                  {p.currently_active === 'Yes' ? (
                    <span style={{ color: '#10B981', fontWeight: 600, fontSize: '0.78rem' }}>Active</span>
                  ) : p.currently_active === 'No' ? (
                    <span style={{ color: '#94A3B8', fontSize: '0.78rem' }}>Inactive</span>
                  ) : '\u2014'}
                </td>
                <td className="num">
                  {p.match_count !== null
                    ? <span style={{ fontWeight: 700, color: '#2196F3' }}>{p.match_count}</span>
                    : <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>—</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CompetitionsTabDrillDownModal
        open={drillPerson != null}
        onClose={() => setDrillPerson(null)}
        variant={drillPerson ? 'person' : null}
        person={drillPerson}
        competitions={[]}
      />
    </div>
  );
}
